import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ClipLoader, PulseLoader } from 'react-spinners';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Transition } from '@headlessui/react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  DocumentTextIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import useEasypayGate from 'hooks/useEasypayGate';

import { eventSchema } from '../../utils/validationSchema';
import { useAlert } from '../../context/AlertContext';
import {
  createEvent,
  getEvent,
  getTemplateEvents,
} from '../../redux/actions/events';
import { categories as allCategories } from '../../utils/categories';
import {
  validateFiles,
  combinedUploadError,
  uploadErrorMessage,
} from '../../utils/uploadValidation';

import Step1 from './Steps/Step1';
import Step2 from './Steps/Step2';
import Step3 from './Steps/Step3';
import Step4 from './Steps/Step4';
import Step5 from './Steps/Step5';

import ConfirmationModal from 'components/ConfirmationModal';
import ProgressRail from './Stepper';

const MAX_ADDITIONAL_IMAGES = 6;

const EventCreation = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const contentRef = useRef(null);

  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // T2B-423: a promoter without Easypay can build the whole event, but it is
  // saved as a draft — publishing waits for the account (the API enforces
  // this too). A connected promoter chooses: publish now, or save a draft.
  const { blocked: publishLocked } = useEasypayGate();
  // Which footer button submitted the form. A ref, not state: it is set in
  // the click handler and read in onSubmit within the same event.
  const submitAsDraftRef = useRef(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedMicroCategories, setSelectedMicroCategories] = useState([]);
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);
  const [showBlueprintUploadModal, setShowBlueprintUploadModal] =
    useState(false);

  // imagery
  const [eventIcon, setEventIcon] = useState(null);
  const [landscapeImage, setLandscapeImage] = useState(null);
  const [regulatoryDocument, setRegulatoryDocument] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [floorplanImage, setFloorplanImage] = useState(null);

  const methods = useForm({
    resolver: yupResolver(eventSchema[step - 1]),
    mode: 'onTouched',
    shouldUnregister: false,
    defaultValues: {
      avatarPreview: null,
      landscapePreview: null,
      additionalPreviews: [],
      regulatoryPreview: null,
      regulatoryName: '',
      eventIcon: null,
      landscapeImage: null,
      morePictures: [],
      regulatoryDocument: null,
      location: '',
      utilities: [],
      requiredDocuments: [],
      locationCoords: { lat: null, lng: null },

      locationPlaceId: '',
      formattedAddress: '',
      address: '',
      neighborhood: '',
      city: '',
      region: '',
      postalCode: '',
      country: '',
      countryCode: '',
      lat: null,
      lng: null,

      spacePlacements: [],
      date: {
        type: 'fixed',
        startDate: '',
        endDate: '',
        openHours: '',
        closeHours: '',
        days: [],
      },
    },
  });

  const steps = [
    {
      label: t('event_create.details'),
      caption: t('event_create.step1_eyebrow'),
    },
    {
      label: t('event_create.media_amenities'),
      caption: t('event_create.step2_eyebrow'),
    },
    {
      label: t('event_create.space_creation'),
      caption: t('event_create.step3_eyebrow'),
    },
    {
      label: t('event_create.floor_plan'),
      caption: t('event_create.step4_eyebrow'),
    },
    {
      label: t('event_create.summary'),
      caption: t('event_create.step5_eyebrow'),
    },
  ];

  const resetScroll = () => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'auto' });
      document.scrollingElement?.scrollTo({ top: 0, behavior: 'auto' });
    }
  };

  const handleOpenTemplatePicker = async () => {
    setShowTemplatePicker(true);
    if (templates.length > 0) return;
    setTemplatesLoading(true);
    try {
      const result = await dispatch(getTemplateEvents());
      setTemplates(result?.data || []);
    } catch {
      // silently fail
    } finally {
      setTemplatesLoading(false);
    }
  };

  const prefillFromEvent = (ev) => {
    methods.setValue('name', ev.name || '');
    methods.setValue('location', ev.location || '');
    methods.setValue('shortDescription', ev.shortDescription || '');
    methods.setValue('longDescription', ev.longDescription || '');

    methods.setValue('locationPlaceId', ev.locationPlaceId || '');
    methods.setValue('formattedAddress', ev.formattedAddress || '');
    methods.setValue('address', ev.address || '');
    methods.setValue('neighborhood', ev.neighborhood || '');
    methods.setValue('city', ev.city || '');
    methods.setValue('region', ev.region || '');
    methods.setValue('postalCode', ev.postalCode || '');
    methods.setValue('country', ev.country || '');
    methods.setValue('countryCode', ev.countryCode || '');
    const lat = ev.lat ?? ev.locationCoords?.lat ?? null;
    const lng = ev.lng ?? ev.locationCoords?.lng ?? null;
    methods.setValue('lat', lat);
    methods.setValue('lng', lng);
    methods.setValue('locationCoords', { lat, lng });

    const socials = ev.socials || {};
    methods.setValue('website', socials.website || ev.website || '');
    methods.setValue('facebookUrl', socials.facebook || ev.facebookUrl || '');
    methods.setValue('twitterUrl', socials.twitter || ev.twitterUrl || '');
    methods.setValue(
      'instagramUrl',
      socials.instagram || ev.instagramUrl || ''
    );
    methods.setValue('linkedinUrl', socials.linkedin || ev.linkedinUrl || '');

    methods.setValue('date', {
      type: 'fixed',
      startDate: '',
      endDate: '',
      openHours: '',
      closeHours: '',
      days: [],
    });

    // categoriesAllowed exists in two DB shapes:
    //   array  — [{ macroCategoryId, microCategoryIds }] (creation flow)
    //   object — { macros: [], micros: [] } (legacy edit flow)
    // Normalize both into the array form before prefilling.
    let rawCats = ev.categoriesAllowed;
    if (rawCats && !Array.isArray(rawCats) && typeof rawCats === 'object') {
      const macros = Array.isArray(rawCats.macros) ? rawCats.macros : [];
      const micros = Array.isArray(rawCats.micros) ? rawCats.micros : [];
      rawCats = macros.map((macroId) => {
        const macroDef = allCategories.find((c) => c.id === macroId);
        const ownMicroIds = new Set((macroDef?.micro || []).map((m) => m.id));
        return {
          macroCategoryId: macroId,
          microCategoryIds: micros.filter((id) => ownMicroIds.has(id)),
        };
      });
    }
    if (Array.isArray(rawCats)) {
      const macroIds = [];
      const microObjs = [];
      for (const item of rawCats) {
        const macroId = item?.macroCategoryId ?? item;
        if (macroId != null) macroIds.push(macroId);
        const microIds = Array.isArray(item?.microCategoryIds)
          ? item.microCategoryIds
          : [];
        const macroDef = allCategories.find((c) => c.id === macroId);
        for (const microId of microIds) {
          const microDef = macroDef?.micro?.find((m) => m.id === microId);
          microObjs.push({
            id: microId,
            macroId,
            icon: microDef?.icon || '',
            name: microDef?.name || '',
          });
        }
      }
      setSelectedCategories(macroIds);
      setSelectedMicroCategories(microObjs);
    }

    if (ev.avatar) {
      setEventIcon({ file: null, preview: ev.avatar });
      methods.setValue('avatarPreview', ev.avatar);
    }
    if (ev.landscapeImage) {
      setLandscapeImage({ file: null, preview: ev.landscapeImage });
      methods.setValue('landscapePreview', ev.landscapeImage);
    }
    const media = ev.media || ev.images || ev.additionalImages || [];
    if (media.length > 0) {
      const imgs = media.slice(0, MAX_ADDITIONAL_IMAGES).map((url, idx) => ({
        id: `tmpl-${idx}`,
        file: null,
        preview: typeof url === 'string' ? url : url?.url || '',
      }));
      setAdditionalImages(imgs);
      methods.setValue(
        'additionalPreviews',
        imgs.map((i) => i.preview)
      );
    }
    if (ev.regulatoryDocument) {
      methods.setValue('regulatoryPreview', ev.regulatoryDocument);
      methods.setValue('regulatoryName', 'Regulatory Document (existing)');
    }

    methods.setValue('utilities', ev.utilities || []);
    methods.setValue('customAmenities', ev.customAmenities || []);
    methods.setValue('requiredDocuments', ev.requiredDocuments || []);

    const spaces = ev.spaces || ev.Spaces || [];
    if (spaces.length > 0) {
      methods.setValue(
        'spaces',
        spaces.map((s) => ({
          name: s.name || '',
          size: s.size != null ? Number(s.size) : undefined,
          description: s.description || '',
          quantity: s.quantity != null ? Number(s.quantity) : undefined,
          price: s.price != null ? Number(s.price) : undefined,
        }))
      );
    }

    if (ev.floorplanUrl) {
      methods.setValue('floorplanPreview', ev.floorplanUrl);
    }
    if (Array.isArray(ev.placements)) {
      // Drop the source event's integer spaceIds: the duplicated event gets
      // brand-new Space rows, and a stale id could collide with one of them
      // on the API's id-first placement linking. Name matching is the
      // reliable key here (space names are unique per event). Placement ids
      // are regenerated too so the copy doesn't share dot ids with the
      // source event (bookings key on placementId per event, but shared ids
      // make logs and debugging needlessly ambiguous).
      methods.setValue(
        'spacePlacements',
        ev.placements.map(({ spaceId, ...rest }) => ({
          ...rest,
          id:
            typeof window !== 'undefined' && window.crypto?.randomUUID
              ? window.crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        }))
      );
    }
  };

  const handleDuplicateTemplate = async (templateId) => {
    if (duplicatingId) return;
    setDuplicatingId(templateId);
    try {
      const eventData = await dispatch(getEvent(templateId));
      if (eventData) {
        prefillFromEvent(eventData);
        setIsDuplicate(true);
        setShowTemplatePicker(false);
        setFormKey((k) => k + 1);
        setStep(1);
        resetScroll();
      }
    } catch {
      showAlert({
        title: t('event_create.template_error_title', 'Duplication failed'),
        message: t(
          'event_create.template_error_message',
          'Something went wrong. Please try again or create from scratch.'
        ),
        primaryAction: { label: 'OK' },
      });
    } finally {
      setDuplicatingId(null);
    }
  };

  const showUploadErrors = (uploadErrors) => {
    showAlert({
      type: 'warning',
      title: t('upload.error_title', { defaultValue: 'Upload issue' }),
      message: uploadErrors.join('\n'),
      primaryAction: { label: t('common.ok') },
    });
  };

  // All files currently attached to the form, for the combined-size budget.
  const attachedUploadFiles = () => [
    eventIcon?.file,
    landscapeImage?.file,
    regulatoryDocument,
    floorplanImage,
    ...(additionalImages || []).map((img) => img?.file),
  ];

  const attachedUploadBytes = () =>
    attachedUploadFiles()
      .filter(Boolean)
      .reduce((sum, f) => sum + (f.size || 0), 0);

  const handleSingleImageUpload = (e, type) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const { accepted, errors: uploadErrors } = validateFiles([file], {
      kind: 'image',
      existingBytes: attachedUploadBytes(),
      t,
    });
    if (uploadErrors.length) {
      showUploadErrors(uploadErrors);
      if (e?.target) e.target.value = '';
      return;
    }
    if (!accepted.length) return;
    const preview = URL.createObjectURL(file);
    const fileData = { file, preview };
    if (type === 'icon') {
      setEventIcon(fileData);
      methods.setValue('avatarPreview', preview);
    } else if (type === 'main') {
      setLandscapeImage(fileData);
      methods.setValue('landscapePreview', preview);
    }
  };

  const handleAdditionalImagesUpload = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;

    const { accepted: files, errors: uploadErrors } = validateFiles(picked, {
      kind: 'image',
      existingBytes: attachedUploadBytes(),
      t,
    });
    if (uploadErrors.length) showUploadErrors(uploadErrors);
    if (!files.length) return;

    setAdditionalImages((prevImgs) => {
      const remaining = MAX_ADDITIONAL_IMAGES - prevImgs.length;
      if (remaining <= 0) return prevImgs;

      const newImages = files.slice(0, remaining).map((file, idx) => ({
        id: crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}-${idx}`,
        file,
        preview: URL.createObjectURL(file),
      }));

      const nextImgs = [...prevImgs, ...newImages];
      const prevPrevs = methods.getValues('additionalPreviews') || [];
      methods.setValue('additionalPreviews', [
        ...prevPrevs,
        ...newImages.map((i) => i.preview),
      ]);
      return nextImgs;
    });

    e.target.value = '';
  };

  const handleRegulatoryUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const { accepted, errors: uploadErrors } = validateFiles([file], {
      kind: 'document',
      existingBytes: attachedUploadBytes(),
      t,
    });
    if (uploadErrors.length) {
      showUploadErrors(uploadErrors);
      e.target.value = '';
      return;
    }
    if (accepted.length) {
      setRegulatoryDocument(file);
      const preview = URL.createObjectURL(file);
      methods.setValue('regulatoryPreview', preview);
      methods.setValue('regulatoryName', file.name);
    }
  };

  const onSubmit = async (data) => {
    if (!agreedToTerms) {
      setTermsError(true);
      return;
    }

    const combinedError = combinedUploadError(attachedUploadFiles(), t);
    if (combinedError) {
      showUploadErrors([combinedError]);
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      const appendIfDefined = (key, value) => {
        if (value === undefined || value === null) return;
        if (typeof value === 'string' && value.trim() === '') return;
        formData.append(key, String(value));
      };

      const isRemoteUrl = (s) =>
        typeof s === 'string' && /^https?:\/\//i.test(s);

      // Floor plan / placements consistency guard.
      // Placements only make sense when a floor plan exists. If the floor plan
      // was skipped or cleared (no uploaded file and no remote URL), never send
      // placements — even if stale placement state lingers in the form (e.g. the
      // user added pins, navigated away, then skipped the floor plan step).
      // Pins are also dropped when their space no longer exists (renamed or
      // deleted in Step 3 after pinning): such orphans would be stored with a
      // dead spaceId and never match a Space row.
      const floorplanPreview = methods.getValues('floorplanPreview');
      const hasFloorplan = !!floorplanImage || isRemoteUrl(floorplanPreview);
      const spaceNames = new Set(
        (data.spaces || []).map((s) => s?.name).filter(Boolean)
      );
      const placementsToSend = hasFloorplan
        ? (data.spacePlacements || []).filter((p) =>
            spaceNames.has(p?.spaceName)
          )
        : [];

      formData.append('name', data.name);
      formData.append('location', data.location);
      formData.append('shortDescription', data.shortDescription);
      formData.append('longDescription', data.longDescription);

      formData.append(
        'socials',
        JSON.stringify({
          website: data.website?.trim() || null,
          facebook: data.facebookUrl?.trim() || null,
          instagram: data.instagramUrl?.trim() || null,
          twitter: data.twitterUrl?.trim() || null,
          linkedin: data.linkedinUrl?.trim() || null,
          tiktok: null,
          others: [],
        })
      );

      formData.append('locationPlaceId', data.locationPlaceId || '');
      appendIfDefined('formattedAddress', data.formattedAddress);
      appendIfDefined('address', data.address);
      appendIfDefined('neighborhood', data.neighborhood);
      appendIfDefined('city', data.city);
      appendIfDefined('region', data.region);
      appendIfDefined('postalCode', data.postalCode);
      appendIfDefined('country', data.country);
      appendIfDefined('countryCode', data.countryCode);

      const lat = data.lat ?? data.locationCoords?.lat ?? null;
      const lng = data.lng ?? data.locationCoords?.lng ?? null;
      appendIfDefined('lat', lat);
      appendIfDefined('lng', lng);

      formData.append('locationCoords', JSON.stringify({ lat, lng }));

      formData.append('spaces', JSON.stringify(data.spaces || []));
      formData.append('placements', JSON.stringify(placementsToSend));
      formData.append('utilities', JSON.stringify(data.utilities || []));
      formData.append(
        'customAmenities',
        JSON.stringify(methods.getValues('customAmenities') || [])
      );
      formData.append(
        'requiredDocuments',
        JSON.stringify(data.requiredDocuments || [])
      );
      formData.append('date', JSON.stringify(data.date));

      const categoriesAllowed = (selectedCategories || []).map((macroId) => ({
        macroCategoryId: macroId,
        microCategoryIds: (selectedMicroCategories || [])
          .filter((micro) => micro.macroId === macroId)
          .map((micro) => micro.id),
      }));
      formData.append('categoriesAllowed', JSON.stringify(categoriesAllowed));

      if (eventIcon?.file) {
        formData.append('avatar', eventIcon.file);
      } else if (isRemoteUrl(eventIcon?.preview)) {
        formData.append('avatar', eventIcon.preview);
      }

      if (landscapeImage?.file) {
        formData.append('landscapeImage', landscapeImage.file);
      } else if (isRemoteUrl(landscapeImage?.preview)) {
        formData.append('landscapeImage', landscapeImage.preview);
      }

      if (regulatoryDocument) {
        formData.append('regulatoryDocument', regulatoryDocument);
      } else {
        const regulatoryPreview = methods.getValues('regulatoryPreview');
        if (isRemoteUrl(regulatoryPreview)) {
          formData.append('regulatoryDocument', regulatoryPreview);
        }
      }

      (additionalImages || []).forEach((img) => {
        if (img?.file) {
          formData.append('media', img.file);
        } else if (isRemoteUrl(img?.preview)) {
          formData.append('media', img.preview);
        }
      });

      // Only send a floor plan when one actually exists; keep it consistent with
      // the placements guard above (no placements are sent without a floor plan).
      if (floorplanImage) {
        formData.append('floorplanUrl', floorplanImage);
      } else if (hasFloorplan) {
        formData.append('floorplanUrl', floorplanPreview);
      }

      const asDraft = publishLocked || submitAsDraftRef.current;
      const eventResponse = await dispatch(
        createEvent(formData, { draft: asDraft })
      );

      if (eventResponse?.id && asDraft) {
        showAlert({
          type: 'success',
          title: t('alert_dialog.event.draft_success_title'),
          message: publishLocked
            ? t('alert_dialog.event.draft_success_message_locked')
            : t('alert_dialog.event.draft_success_message'),
          primaryAction: {
            label: t('alert_dialog.event.create_success_primary_action'),
          },
        });
        navigate('/dashboard?tab=my-events&list=drafts');
      } else if (eventResponse?.id) {
        // Published: My Events opens with the share pop-up (link, Instagram
        // images, QR code) for the new event.
        navigate('/dashboard?tab=my-events', {
          state: { publishedEvent: eventResponse },
        });
      } else {
        showAlert({
          type: 'error',
          title: t('alert_dialog.event.create_error_title'),
          message: t('alert_dialog.event.create_error_message'),
          primaryAction: {
            label: t('alert_dialog.event.create_error_primary_action'),
          },
        });
      }
    } catch (error) {
      const uploadMessage = uploadErrorMessage(error, t);
      // The profile said connected but the API disagreed (e.g. the account id
      // was removed meanwhile): say so plainly — the promoter can still save
      // this as a draft from the same screen.
      const easypayMessage =
        error?.response?.data?.code === 'EASYPAY_ACCOUNT_REQUIRED'
          ? t('event_create.publish_locked_error')
          : null;
      const serverMessage =
        easypayMessage ||
        error?.response?.data?.message ||
        error?.response?.data?.error;
      showAlert({
        type: 'error',
        title: t('alert_dialog.event.create_error_title'),
        message:
          uploadMessage ||
          serverMessage ||
          t('alert_dialog.event.create_error_message'),
        primaryAction: {
          label: t('alert_dialog.event.create_error_primary_action'),
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const clearSingleImage = (type) => {
    if (type === 'icon') {
      if (eventIcon?.preview) URL.revokeObjectURL(eventIcon.preview);
      setEventIcon(null);
      methods.setValue('avatarPreview', null);
    } else if (type === 'main') {
      if (landscapeImage?.preview) URL.revokeObjectURL(landscapeImage.preview);
      setLandscapeImage(null);
      methods.setValue('landscapePreview', null);
    }
  };

  const removeAdditionalImage = (id) => {
    setAdditionalImages((prev) => {
      const next = prev.filter((img) => img.id !== id);
      const removed = prev.find((img) => img.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      methods.setValue(
        'additionalPreviews',
        next.map((i) => i.preview)
      );
      return next;
    });
  };

  const clearRegulatoryUpload = () => {
    const prev = methods.getValues('regulatoryPreview');
    if (prev) URL.revokeObjectURL(prev);
    setRegulatoryDocument(null);
    methods.setValue('regulatoryPreview', null);
    methods.setValue('regulatoryName', '');
  };

  useEffect(() => {
    return () => {
      if (eventIcon?.preview) URL.revokeObjectURL(eventIcon.preview);
      if (landscapeImage?.preview) URL.revokeObjectURL(landscapeImage.preview);
      additionalImages?.forEach((img) => {
        if (img?.preview) URL.revokeObjectURL(img.preview);
      });
      const pdfPrev = methods.getValues('regulatoryPreview');
      if (pdfPrev) URL.revokeObjectURL(pdfPrev);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const LEAVE_MS = 280;

  const handleStepChange = async (newStep) => {
    if (newStep === step) return;
    const isGoingBack = newStep < step;

    const run = () => {
      setIsTransitioning(true);
      setTimeout(() => {
        setStep(newStep);
        requestAnimationFrame(() => {
          resetScroll();
          requestAnimationFrame(resetScroll);
        });
        setIsTransitioning(false);
      }, LEAVE_MS);
    };

    if (isGoingBack) {
      run();
      return;
    }

    if (step === 1 && newStep === 2) {
      if (!selectedCategories || selectedCategories.length === 0) {
        methods.setError('categoriesAllowed', {
          type: 'manual',
          message: t('event_create.validation.required_macro_category'),
        });
        requestAnimationFrame(() => {
          document
            .querySelector('[name="categoriesAllowed"]')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        return;
      } else {
        methods.clearErrors('categoriesAllowed');
      }
    }

    const isValid = await methods.trigger(undefined, { shouldFocus: true });
    if (isValid) {
      run();
    } else {
      const firstErrorName = Object.keys(methods.formState.errors)[0];
      if (firstErrorName) {
        const el = document.querySelector(`[name="${firstErrorName}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.focus?.();
      }
    }
  };

  // The per-step resolver itself needs no wiring here: useForm re-reads its
  // options every render, so `yupResolver(eventSchema[step - 1])` above
  // already tracks the current step.
  useEffect(() => {
    methods.reset(methods.getValues(), {
      keepErrors: true,
      keepDirty: true,
      keepValues: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Show template banner only on Step 1, no name typed yet, not already duplicated.
  const watchedName = methods.watch('name') || '';
  const showTemplateBanner = step === 1 && !isDuplicate && !watchedName.trim();

  const caption = steps[step - 1]?.caption;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 md:px-8 py-6">
        <div className="bg-white rounded-3xl shadow-card border border-black/5 flex flex-col">
          {/* Header */}
          <header className="px-6 md:px-8 py-4 flex items-center justify-between border-b border-line gap-4">
            <h1 className="text-[17px] font-extrabold text-ink-strong tracking-tight truncate">
              {t('event_create.title')}
            </h1>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-3 py-1.5 rounded-full bg-paper-2 border border-line text-[11px] font-bold text-ink-strong hover:bg-paper transition shrink-0"
            >
              {t('event_create.exit', 'Exit')}
            </button>
          </header>

          {/* Template banner (Step 1 only, pre-name) */}
          {showTemplateBanner && (
            <div className="px-6 md:px-8 pt-5">
              <div className="rounded-2xl bg-paper-2 border border-line px-5 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-8 h-8 grid place-items-center rounded-lg bg-brand text-ink-strong shrink-0">
                    <SparklesIcon className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-bold text-ink-strong truncate">
                      {t('event_create.template_banner_title')}
                    </p>
                    <p className="text-[11px] text-muted truncate">
                      {t('event_create.template_banner_subtitle')}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    showTemplatePicker
                      ? setShowTemplatePicker(false)
                      : handleOpenTemplatePicker()
                  }
                  className="shrink-0 px-3.5 py-2 rounded-full bg-white border border-line text-[11px] font-bold text-ink-strong hover:bg-paper-2 transition whitespace-nowrap"
                >
                  {showTemplatePicker
                    ? t('event_create.template_hide')
                    : t('event_create.template_show')}
                </button>
              </div>

              {showTemplatePicker && (
                <div className="mt-3 rounded-2xl border border-line bg-white p-4">
                  {templatesLoading ? (
                    <div className="flex justify-center py-6">
                      <PulseLoader color="#FFBD59" size={10} />
                    </div>
                  ) : templates.length === 0 ? (
                    <p className="text-muted text-[12.5px] text-center py-4">
                      {t(
                        'event_create.template_empty',
                        'No previous events found.'
                      )}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {templates.map((tmpl) => (
                        <button
                          key={tmpl.id}
                          type="button"
                          disabled={duplicatingId === tmpl.id}
                          onClick={() => handleDuplicateTemplate(tmpl.id)}
                          className="flex items-center gap-3 p-3 rounded-xl border border-line hover:border-brand hover:bg-brand-softer transition text-left disabled:opacity-50"
                        >
                          {tmpl.avatar ? (
                            <img
                              src={tmpl.avatar}
                              alt={tmpl.name}
                              className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-paper-2 border border-line grid place-items-center flex-shrink-0">
                              <span className="text-gray-300 text-lg">?</span>
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-[13px] font-bold text-ink-strong truncate">
                              {tmpl.name}
                            </p>
                            <p className="text-[11px] text-muted truncate">
                              {tmpl.city && tmpl.country
                                ? `${tmpl.city}, ${tmpl.country}`
                                : tmpl.location || ''}
                            </p>
                          </div>
                          {duplicatingId === tmpl.id && (
                            <ClipLoader color="#FFBD59" size={18} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Duplicated notice */}
          {isDuplicate && step === 1 && (
            <div className="px-6 md:px-8 pt-5">
              <div className="rounded-2xl bg-brand-softer border border-amber-200 px-5 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-8 h-8 grid place-items-center rounded-lg bg-brand text-ink-strong shrink-0">
                    <CheckIcon className="w-4 h-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-bold text-ink-strong truncate">
                      {t(
                        'event_create.template_loaded_title',
                        'Template loaded — review and complete each step'
                      )}
                    </p>
                    <p className="text-[11px] text-muted truncate">
                      {t(
                        'event_create.template_loaded_subtitle',
                        'All fields are pre-filled except dates. Go through each step and submit when ready.'
                      )}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsDuplicate(false)}
                  className="w-8 h-8 grid place-items-center rounded-full text-gray-400 hover:bg-paper-2 hover:text-ink-strong shrink-0"
                  aria-label="dismiss"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Progress rail */}
          <div className="px-6 md:px-8 pt-6 pb-5 bg-white border-b border-line">
            <ProgressRail
              steps={steps}
              currentStep={step}
              onJump={(n) => {
                if (n === step) return;
                if (n < step) {
                  if (step === 4 && n < 4) {
                    setShowBackConfirmation(true);
                    return;
                  }
                  handleStepChange(n);
                } else {
                  // Leaving Step 4 forward must go through its Save/Skip
                  // buttons — they own the placement-completeness guards
                  // (all units placed, none over-placed), which the shared
                  // schema validation doesn't cover. The footer already
                  // points there ("use Save floor plan").
                  if (step === 4) return;
                  handleStepChange(n);
                }
              }}
            />
          </div>

          {/* Step body */}
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              className="flex flex-col"
            >
              <main
                ref={contentRef}
                className="px-6 md:px-8 py-8 bg-paper-2/40"
              >
                <Transition
                  as="div"
                  show={!isTransitioning}
                  appear
                  unmount
                  enter="transition ease-out duration-300"
                  enterFrom="transform opacity-0 translate-y-3"
                  enterTo="transform opacity-100 translate-y-0"
                  leave="transition ease-in duration-200"
                  leaveFrom="transform opacity-100 translate-y-0"
                  leaveTo="transform opacity-0 translate-y-3"
                  afterEnter={resetScroll}
                >
                  <div className="step-enter" key={`step-${step}`}>
                    {step === 1 && (
                      <Step1
                        key={`step1-${formKey}`}
                        methods={methods}
                        selectedCategories={selectedCategories}
                        setSelectedCategories={setSelectedCategories}
                        selectedMicroCategories={selectedMicroCategories}
                        setSelectedMicroCategories={setSelectedMicroCategories}
                      />
                    )}
                    {step === 2 && (
                      <Step2
                        key={`step2-${formKey}`}
                        methods={methods}
                        eventIcon={eventIcon}
                        landscapeImage={landscapeImage}
                        additionalImages={additionalImages}
                        regulatoryDocument={regulatoryDocument}
                        handleSingleImageUpload={handleSingleImageUpload}
                        handleAdditionalImagesUpload={
                          handleAdditionalImagesUpload
                        }
                        handleRegulatoryUpload={handleRegulatoryUpload}
                        clearSingleImage={clearSingleImage}
                        removeAdditionalImage={removeAdditionalImage}
                        clearRegulatoryUpload={clearRegulatoryUpload}
                      />
                    )}
                    {step === 3 && (
                      <Step3 key={`step3-${formKey}`} methods={methods} />
                    )}
                    {step === 4 && (
                      <Step4
                        key={`step4-${formKey}`}
                        methods={methods}
                        setStep={setStep}
                        showBlueprintUploadModal={showBlueprintUploadModal}
                        setShowBlueprintUploadModal={
                          setShowBlueprintUploadModal
                        }
                        setFloorplanImage={setFloorplanImage}
                      />
                    )}
                    {step === 5 && (
                      <Step5
                        key={`step5-${formKey}`}
                        methods={methods}
                        goToStep={(n) => handleStepChange(n)}
                        agreedToTerms={agreedToTerms}
                        termsError={termsError}
                        publishLocked={publishLocked}
                        onAgreeChange={(v) => {
                          setAgreedToTerms(v);
                          if (v) setTermsError(false);
                        }}
                      />
                    )}
                  </div>
                </Transition>
              </main>

              {/* Footer nav */}
              <footer className="px-6 md:px-8 py-5 border-t border-line bg-white flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (step === 1) return;
                    if (step === 4) {
                      setShowBackConfirmation(true);
                    } else {
                      handleStepChange(step - 1);
                    }
                  }}
                  disabled={step === 1}
                  className={[
                    'inline-flex items-center justify-center gap-2 rounded-full font-bold transition h-10 px-4 text-[13px]',
                    'bg-transparent text-gray-600 hover:bg-paper-2 hover:text-ink-strong',
                    step === 1 ? 'opacity-30 cursor-not-allowed' : '',
                  ].join(' ')}
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  {t('event_create.previous_step')}
                </button>

                <div className="hidden sm:flex items-center gap-2 text-[11px] text-muted">
                  <span className="font-bold tabular-nums text-ink-strong">
                    {step}
                  </span>
                  <span>{t('event_create.of', 'of')}</span>
                  <span className="font-bold tabular-nums">{steps.length}</span>
                  {caption && (
                    <>
                      <span className="mx-2">·</span>
                      <span className="truncate max-w-[260px]">{caption}</span>
                    </>
                  )}
                </div>

                {step < 5 ? (
                  step === 4 ? (
                    <span className="text-[11px] text-muted">
                      {t('event_create.use_save_floor_plan')}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleStepChange(step + 1)}
                      className="inline-flex items-center justify-center gap-2 rounded-full font-bold transition h-10 px-5 text-[13px] bg-brand text-ink-strong hover:bg-brand-hover"
                    >
                      {t('event_create.continue', 'Continue')}
                      <ArrowRightIcon className="w-4 h-4" />
                    </button>
                  )
                ) : (
                  <div className="flex items-center gap-2">
                    {/* Without Easypay, saving a draft is the only way out of
                        the wizard, so it takes the primary style. */}
                    <button
                      type="submit"
                      onClick={() => {
                        submitAsDraftRef.current = true;
                      }}
                      disabled={loading || !agreedToTerms}
                      className={[
                        'inline-flex items-center justify-center gap-2 rounded-full font-bold transition h-10 px-5 text-[13px] disabled:opacity-60 disabled:cursor-not-allowed',
                        publishLocked
                          ? 'bg-brand text-ink-strong hover:bg-brand-hover'
                          : 'bg-white text-ink-strong ring-1 ring-line hover:bg-paper-2',
                      ].join(' ')}
                    >
                      {loading &&
                      (publishLocked || submitAsDraftRef.current) ? (
                        <ClipLoader color="#1A1A1F" size={16} />
                      ) : (
                        <>
                          <DocumentTextIcon className="w-4 h-4" />
                          {t('event_create.save_draft')}
                        </>
                      )}
                    </button>
                    {!publishLocked && (
                      <button
                        type="submit"
                        onClick={() => {
                          submitAsDraftRef.current = false;
                        }}
                        disabled={loading || !agreedToTerms}
                        className="inline-flex items-center justify-center gap-2 rounded-full font-bold transition h-10 px-5 text-[13px] bg-brand text-ink-strong hover:bg-brand-hover disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {loading && !submitAsDraftRef.current ? (
                          <ClipLoader color="#1A1A1F" size={16} />
                        ) : (
                          <>
                            {t('event_create.publish_event', 'Publish event')}
                            <CheckIcon className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </footer>
            </form>
          </FormProvider>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showBackConfirmation}
        title={t('event_create.previous_changes_title')}
        message={t('event_create.previous_changes_message')}
        onConfirm={() => {
          setShowBackConfirmation(false);
          setStep(3);
        }}
        onCancel={() => setShowBackConfirmation(false)}
      />
    </div>
  );
};

export default EventCreation;
